import {
  Box,
  BoxProps,
  Center,
  HStack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';
import { AfeEacResponse } from 'api/bindings/AfeEacResponse';
import { AfeResponse } from 'api/bindings/AfeResponse';
import { AppInput, CurrencyLabel, MultiBar } from 'features/components';
import { useMemo } from 'react';
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
} from 'react-hook-form';

interface Row extends AfeEacResponse {
  newEac: string;
}
interface Form {
  lineItems: Row[];
}

export interface AfeEacLineItemsSectionProps extends BoxProps {
  afe: AfeResponse | undefined;
}

export const AfeEacLineItemsSection = (props: AfeEacLineItemsSectionProps) => {
  const { afe, ...others } = props;

  const lineItems = useMemo(() => {
    return afe?.eacLineItems.map((eac) => {
      return {
        ...eac,
        newEac: eac.eac,
      };
    });
  }, [afe?.eacLineItems]);

  const form = useForm<Form>({
    defaultValues: {
      lineItems,
    },
  });
  const liFa = useFieldArray({
    control: form.control,
    name: 'lineItems',
    keyName: '_id',
    rules: {
      validate: {
        atLeastOne: (v) =>
          v.findIndex((x) => x.eac !== x.newEac) >= 0 ||
          'At least one row must have a new EAC value.',
      },
    },
  });

  return (
    <Box {...others}>
      <FormProvider {...form}>
        <TableContainer>
          <Table>
            <Thead>
              <Tr>
                <Th>
                  <Center>Account</Center>
                </Th>
                <Th>
                  <Center>Budget vs. Contingency</Center>
                </Th>
                <Th>
                  <Center>Commitment</Center>
                </Th>
                <Th>
                  <Center>Invoice</Center>
                </Th>
                <Th>
                  <Center>EAC</Center>
                </Th>
                <Th>
                  <Center>New EAC</Center>
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {liFa.fields.map((li) => (
                <Tr key={li._id}>
                  <Td>{li.subAccount.fullSubAccountName}</Td>
                  <Td>
                    <VStack>
                      <HStack>
                        <Text as="b">Total:</Text>
                        <CurrencyLabel value={li.totalBudget} />
                        <Text as="b">/</Text>
                        <CurrencyLabel value={li.totalContingency} />
                      </HStack>
                      <MultiBar
                        values={[li.totalBudget, li.totalContingency]}
                      />
                      <MultiBar
                        values={[li.pendingBudget, li.pendingContingency]}
                      />
                      <HStack>
                        <Text as="b">Pending:</Text>
                        <CurrencyLabel value={li.pendingBudget} />
                        <Text as="b">/</Text>
                        <CurrencyLabel value={li.pendingContingency} />
                      </HStack>
                    </VStack>
                  </Td>
                  <Td>
                    <VStack>
                      <HStack>
                        <Text as="b">Total:</Text>
                        <CurrencyLabel value={li.totalCommitment} />
                      </HStack>
                      <MultiBar
                        max={[li.totalBudget, li.totalContingency]}
                        values={[li.totalCommitment]}
                      />
                      <MultiBar
                        max={[li.totalBudget, li.totalContingency]}
                        values={[li.pendingCommitment]}
                      />
                      <HStack>
                        <Text as="b">Pending:</Text>
                        <CurrencyLabel value={li.pendingCommitment} />
                      </HStack>
                    </VStack>
                  </Td>
                  <Td>
                    <VStack>
                      <HStack>
                        <Text as="b">Total:</Text>
                        <CurrencyLabel value={li.totalInvoice} />
                      </HStack>
                      <MultiBar
                        max={[li.totalBudget, li.totalContingency]}
                        values={[li.totalInvoice]}
                      />
                      <MultiBar
                        max={[li.totalBudget, li.totalContingency]}
                        values={[li.pendingInvoice]}
                      />
                      <HStack>
                        <Text as="b">Pending:</Text>
                        <CurrencyLabel value={li.pendingInvoice} />
                      </HStack>
                    </VStack>
                  </Td>
                  <Td>
                    <VStack>
                      <CurrencyLabel value={li.eac} />
                      <MultiBar
                        max={[li.totalBudget, li.totalContingency]}
                        values={[li.eac]}
                      />
                    </VStack>
                  </Td>
                  <Td>
                    <Controller
                      control={form.control}
                      name={`lineItems[${li._id}]` as any}
                      render={(p) => <AppInput mode="currency" {...p.field} />}
                    />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </FormProvider>
    </Box>
  );
};

// interface InlineCellDisplayProps extends StackProps {
//   budget: string | undefined;
//   total: string | undefined;
//   pending: string | undefined;
// }

// function InlineCellDisplay(props: InlineCellDisplayProps) {
//   const { total, pending, budget, ...others } = props;
//   const [first, second, third, fourth] = useProgressBarColors();

//   const data = useMemo(() => {
//     const dBudget = new Decimal(budget || 0);
//     const dTotal = new Decimal(total || 0);
//     const dPending = new Decimal(pending || 0);

//     const ratios = {
//       total: dTotal.div(dBudget).times(100).toNumber(),
//       pending: dPending.div(dBudget).times(100).toNumber(),
//     };
//     const totalElements = [{ color: first, value: ratios.total }];
//     const pendingElements = [{ color: second, value: ratios.pending }];

//     return {
//       ratios,
//       totalElements,
//       pendingElements,
//     };
//   }, [budget, first, pending, second, total]);

//   return (
//     <VStack {...others}>
//       <HStack>
//         <Text as="b">Total:</Text>
//         <CurrencyLabel value={total} />
//       </HStack>
//       <MultiBar elements={data.totalElements} />
//       {/* <MultiProgress elements={data.totalElements} /> */}
//       <MultiBar elements={data.pendingElements} />
//       {/* <MultiProgress elements={data.pendingElements} /> */}
//       <HStack>
//         <Text as="b">Pending:</Text>
//         <CurrencyLabel value={pending} />
//       </HStack>
//     </VStack>
//   );
// }
